import React, { useState } from "react";
import axios from "axios";
import Links from "../../Data/Links";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const MarketingBudgetUpload = () => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [uploadResults, setUploadResults] = useState(null);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        const fileExtension = selectedFile?.name.split(".").pop().toLowerCase();
        if (selectedFile && fileExtension === "csv") {
            setFile(selectedFile);
            setMessage({ type: "", text: "" });
            setUploadResults(null);
        } else {
            setMessage({ type: "error", text: "Please select a CSV file" });
            setFile(null);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            setMessage({ type: "error", text: "Please select a CSV file" });
            return;
        }

        setLoading(true);
        setMessage({ type: "", text: "" });
        setUploadResults(null);

        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await axios.post(Links.uploadMarketingTargets, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            setUploadResults({
                total: response.data?.totalRecords ?? 0,
                success: response.data?.successCount ?? 0,
                failed: response.data?.failedCount ?? 0,
                errors: response.data?.errors ?? [],
            });

            setMessage({
                type: "success",
                text: `Uploaded ${response.data?.successCount ?? 0} of ${response.data?.totalRecords ?? 0} rows`,
            });
            setFile(null);
            const input = document.getElementById("marketing-file-input");
            if (input) input.value = "";
        } catch (err) {
            setMessage({
                type: "error",
                text: err.response?.data?.message || err.message || "Upload failed",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ margin: "2rem" }}>
            <h2 style={{ marginBottom: "1rem" }}>Marketing Budget Upload</h2>
            <p style={{ marginBottom: "1.5rem", color: "#555" }}>
                Upload marketing targets in CSV format; data will be parsed and written to the database.
            </p>

            <div style={{
                padding: "1.5rem",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                marginBottom: "2rem",
            }}>
                <h4 style={{ marginBottom: "1rem" }}>Upload CSV</h4>

                <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1.5rem" }}>
                    <input
                        id="marketing-file-input"
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                    />
                    <label htmlFor="marketing-file-input">
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                            size="large"
                        >
                            Choose CSV
                        </Button>
                    </label>
                    {file && (
                        <div style={{
                            padding: "0.5rem 1rem",
                            backgroundColor: "#e3f2fd",
                            borderRadius: "4px",
                            fontWeight: 500,
                        }}>
                            {file.name}
                        </div>
                    )}
                </div>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpload}
                    disabled={!file || loading}
                    size="large"
                    style={{ minWidth: "160px" }}
                >
                    {loading ? <CircularProgress size={22} color="inherit" /> : "Upload"}
                </Button>

                {message.text && (
                    <div style={{ marginTop: "1rem" }}>
                        <Alert
                            severity={message.type}
                            onClose={() => setMessage({ type: "", text: "" })}
                        >
                            {message.text}
                        </Alert>
                    </div>
                )}

                {uploadResults && (
                    <div style={{
                        marginTop: "1.5rem",
                        padding: "1rem",
                        backgroundColor: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: "4px"
                    }}>
                        <h5 style={{ marginBottom: "0.5rem" }}>Upload Results</h5>
                        <div style={{ marginBottom: "0.5rem" }}>
                            <strong>Total Records:</strong> {uploadResults.total}
                        </div>
                        <div style={{ marginBottom: "0.5rem" }}>
                            <strong>Successful:</strong> <span style={{ color: "green" }}>{uploadResults.success}</span>
                        </div>
                        <div style={{ marginBottom: "0.5rem" }}>
                            <strong>Failed:</strong> <span style={{ color: "red" }}>{uploadResults.failed}</span>
                        </div>
                        {uploadResults.errors && uploadResults.errors.length > 0 && (
                            <div style={{ marginTop: "1rem" }}>
                                <strong>Errors:</strong>
                                <ul style={{ marginTop: "0.5rem", paddingLeft: "1.5rem" }}>
                                    {uploadResults.errors.map((error, idx) => (
                                        <li key={idx} style={{ color: "red", fontSize: "0.9rem" }}>
                                            {typeof error === 'string' ? error : `Row ${error.row}${error.store ? ` (${error.store})` : ''}: ${error.message}`}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div style={{
                padding: "1rem",
                backgroundColor: "#fffbf0",
                border: "1px solid #ffe066",
                borderRadius: "4px",
                fontSize: "0.95rem",
            }}>
                <strong>Notes:</strong>
                <ul style={{ margin: "0.5rem 0 0 1rem" }}>
                    <li>Required columns: store_name, month, year, fup_target, internet_target, opportunities_target, unique_visitors_target, vdp_views_target</li>
                    <li>CSV only; the backend handles transformation and database write.</li>
                    <li>Each row in the CSV should include its own month and year values.</li>
                </ul>
            </div>
        </div>
    );
};

export default MarketingBudgetUpload;
